import { UserCog, Info } from 'lucide-react'
import type { Gerant } from '../types'

interface Props {
  gerant: Gerant
  onChange: (g: Gerant) => void
}

const fmt0 = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)

export default function SectionGerant({ gerant, onChange }: Props) {
  const set = <K extends keyof Gerant>(key: K, v: Gerant[K]) => onChange({ ...gerant, [key]: v })

  const heuresProd = gerant.heuresMois * (gerant.tauxEfficacite / 100)

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      {/* ── En-tête ── */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100"
        style={{ background: 'linear-gradient(135deg, #1A3C6E08, #1A3C6E15)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: '#1A3C6E' }}>
          <UserCog size={16} className="text-white" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-gray-800">Gérant / Dirigeant</h2>
          <p className="text-xs text-gray-500">Rémunération prise en compte dans le seuil de rentabilité</p>
        </div>
      </div>

      <div className="p-4 space-y-4">

        {/* ── Info contextuelle ── */}
        <div className="flex gap-2 p-3 rounded-xl text-xs" style={{ background: '#EFF6FF', color: '#1A3C6E' }}>
          <Info size={14} className="shrink-0 mt-0.5" style={{ color: '#1A3C6E' }} />
          <span>
            Renseignez ici le <strong>coût total mensuel du gérant</strong> pour le salon
            (rémunération nette + cotisations sociales TNS ou charges patronales si salarié).
            Ce montant est toujours intégré dans le seuil, que le gérant soit actif ou passif.
          </span>
        </div>

        {/* ── Champs de saisie ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">

          {/* Coût total mensuel */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">
              Coût total mensuel (€)
            </label>
            <div className="relative">
              <input
                type="number" min={0} step={100}
                value={gerant.coutTotal || ''}
                placeholder="0"
                onChange={e => set('coutTotal', +e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 pr-8
                           focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Rémunération + charges sociales</p>
          </div>

          {/* Heures / mois */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">
              Heures / mois
            </label>
            <div className="relative">
              <input
                type="number" min={0} max={300} step={1}
                value={gerant.heuresMois || ''}
                placeholder="0"
                onChange={e => set('heuresMois', +e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 pr-8
                           focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">h</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Utilisé en mode <strong>Gérant actif</strong>
            </p>
          </div>

          {/* Taux d'efficacité */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">
              Taux d'efficacité (%)
            </label>
            <div className="relative">
              <input
                type="number" min={0} max={100} step={5}
                value={gerant.tauxEfficacite}
                onChange={e => set('tauxEfficacite', Math.min(100, Math.max(0, +e.target.value)))}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 pr-8
                           focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">% agenda en cabine</p>
          </div>
        </div>

        {/* ── Récapitulatif ── */}
        {gerant.coutTotal > 0 && (
          <div className="grid grid-cols-2 gap-3 pt-1">

            {/* Coût total */}
            <div className="rounded-xl p-3 text-center" style={{ background: '#1A3C6E12' }}>
              <div className="text-xs text-gray-500 mb-0.5">Coût mensuel gérant</div>
              <div className="text-lg font-black" style={{ color: '#1A3C6E' }}>
                {fmt0(gerant.coutTotal)}
              </div>
              <div className="text-xs text-gray-400">intégré dans le seuil</div>
            </div>

            {/* Heures productives */}
            <div className="rounded-xl p-3 text-center"
              style={{ background: heuresProd > 0 ? '#EFF6FF' : '#F9FAFB' }}>
              <div className="text-xs text-gray-500 mb-0.5">Heures productives</div>
              <div className="text-lg font-black" style={{ color: heuresProd > 0 ? '#1A3C6E' : '#9CA3AF' }}>
                {Math.round(heuresProd)} h
              </div>
              <div className="text-xs text-gray-400">
                {heuresProd > 0 ? 'comptées en mode actif' : 'non comptées (passif)'}
              </div>
            </div>
          </div>
        )}

        {/* ── Rappel mode gérant ── */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg p-2.5 border border-blue-100" style={{ background: '#F0F7FF' }}>
            <div className="font-semibold mb-0.5" style={{ color: '#1A3C6E' }}>✍️ Mode Actif</div>
            <p className="text-gray-500">Le gérant génère du CA. Ses heures comptent dans le calcul du coût-minute. Le seuil est réparti sur toute l'équipe.</p>
          </div>
          <div className="rounded-lg p-2.5 border border-amber-100" style={{ background: '#FFF7ED' }}>
            <div className="font-semibold mb-0.5 text-amber-800">👑 Mode Passif</div>
            <p className="text-gray-500">Le gérant ne fait pas de CA. L'équipe seule doit couvrir <em>toutes</em> les charges (y compris sa rémunération). Le coût-minute de l'équipe augmente.</p>
          </div>
        </div>

      </div>
    </div>
  )
}
